import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  decideWorkflowHumanValidation,
  fetchTimeeventById,
  fetchValidationLocks,
  fetchWorkflowHumanValidations,
} from '../Utils/timeEventsApi';
import { useUser } from './WebSocketContext';
import { getViewerTypeFromFilename, useFileViewer, VIEWER_COMPONENTS } from './Viewer/FileViewerProvider';
import ActiveValidationLocksMenu from './ActiveValidationLocksMenu';

const TimeEvent = React.lazy(() => import('./Timevents/TimeEvent'));

const PAGE_SIZE = 100;

export default function HumanValidationWorkspace() {
  const { caseid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isValidator, username } = useUser();
  const { closeFileViewer } = useFileViewer();
  const [requests, setRequests] = useState([]);
  const [index, setIndex] = useState(0);
  const [event, setEvent] = useState(null);
  const [checkedItems, setCheckedItems] = useState([]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [eventLoadStatus, setEventLoadStatus] = useState('idle');
  const [eventLoadError, setEventLoadError] = useState('');
  const [eventLoadAttempt, setEventLoadAttempt] = useState(0);
  const [eventLoadedRequestId, setEventLoadedRequestId] = useState(null);
  const [, setPdfViewerUrl] = useState(null);
  const [validationLocks, setValidationLocks] = useState([]);

  const currentRequest = requests[index] || null;
  const reviewItems = useMemo(
    () => Array.isArray(currentRequest?.review_items) ? currentRequest.review_items : [],
    [currentRequest],
  );
  const allItemsConfirmed = reviewItems.length === 0
    || reviewItems.every((_, itemIndex) => checkedItems.includes(itemIndex));
  const isEventValidation = currentRequest?.target_type === 'event';
  const eventPreviewReady = !isEventValidation
    || (Boolean(currentRequest?.timeevent_id)
      && eventLoadStatus === 'ready'
      && eventLoadedRequestId === currentRequest.id);
  const queueTotal = Math.max(requests.length, totalCount, 1);
  const progressValue = currentRequest
    ? Math.min(100, ((index + 1) / queueTotal) * 100)
    : 0;
  const noopSetFilteredTimeEvents = useCallback(() => {}, []);
  const noopEventAction = useCallback(() => {}, []);
  const viewerType = getViewerTypeFromFilename(event?.eventsource);
  const ViewerComponent = VIEWER_COMPONENTS[viewerType] || VIEWER_COMPONENTS.other;
  const validationLocksForDisplay = useMemo(() => {
    const locksByEventId = new Map(
      (Array.isArray(validationLocks) ? validationLocks : []).map((lock) => [Number(lock.event_id), lock]),
    );
    const currentEventId = Number(event?.id);
    if (
      Number.isFinite(currentEventId)
      && currentEventId > 0
      && currentRequest?.id != null
      && eventLoadedRequestId === currentRequest.id
      && !locksByEventId.has(currentEventId)
    ) {
      locksByEventId.set(currentEventId, { event_id: currentEventId, username });
    }
    return Array.from(locksByEventId.values());
  }, [currentRequest?.id, event?.id, eventLoadedRequestId, username, validationLocks]);

  const loadRequests = useCallback(async () => {
    if (!caseid) return;
    setLoading(true);
    setError('');
    try {
      const payload = await fetchWorkflowHumanValidations(caseid, {
        page: 1,
        pageSize: PAGE_SIZE,
        offset: 0,
        status: 'pending',
      });
      const pendingRequests = Array.isArray(payload?.requests) ? payload.requests : [];
      setRequests(pendingRequests);
      setTotalCount(Number(payload?.total_count || pendingRequests.length));
      setIndex(0);
    } catch (requestError) {
      setError(requestError?.message || t('validation_workspace.flow_load_error'));
    } finally {
      setLoading(false);
    }
  }, [caseid, t]);

  const loadMoreRequests = useCallback(async () => {
    if (!caseid || loadingMore || requests.length >= totalCount) return 0;
    setLoadingMore(true);
    setError('');
    try {
      const payload = await fetchWorkflowHumanValidations(caseid, {
        page: Math.floor(requests.length / PAGE_SIZE) + 1,
        pageSize: PAGE_SIZE,
        offset: requests.length,
        status: 'pending',
      });
      const receivedRequests = Array.isArray(payload?.requests) ? payload.requests : [];
      const knownRequestIds = new Set(requests.map((request) => String(request.id)));
      const newRequests = receivedRequests.filter(
        (request) => request?.id != null && !knownRequestIds.has(String(request.id)),
      );
      setRequests((previous) => {
        const previousIds = new Set(previous.map((request) => String(request.id)));
        return [
          ...previous,
          ...newRequests.filter((request) => !previousIds.has(String(request.id))),
        ];
      });
      setTotalCount(Number(payload?.total_count || totalCount));
      return newRequests.length;
    } catch (requestError) {
      setError(requestError?.message || t('validation_workspace.flow_load_error'));
      return 0;
    } finally {
      setLoadingMore(false);
    }
  }, [caseid, loadingMore, requests, t, totalCount]);

  const handleNext = useCallback(async () => {
    if (deciding || loadingMore) return;
    if (index < requests.length - 1) {
      setIndex((current) => current + 1);
      return;
    }
    const loadedCount = await loadMoreRequests();
    if (loadedCount > 0) {
      setIndex((current) => current + 1);
    }
  }, [deciding, index, loadMoreRequests, loadingMore, requests.length]);

  const handlePrevious = useCallback(() => {
    if (deciding) return;
    setIndex((current) => Math.max(0, current - 1));
  }, [deciding]);

  useEffect(() => {
    if (isValidator) loadRequests();
  }, [isValidator, loadRequests]);

  useEffect(() => {
    if (!caseid || !isValidator) return undefined;
    const refreshLocks = () => fetchValidationLocks(caseid).then(setValidationLocks).catch(() => {});
    refreshLocks();
    const timer = window.setInterval(refreshLocks, 15000);
    return () => window.clearInterval(timer);
  }, [caseid, isValidator]);

  useEffect(() => {
    closeFileViewer();
  }, [closeFileViewer, currentRequest?.id]);

  useEffect(() => {
    setCheckedItems([]);
    setComment('');
    setEvent(null);
    setEventLoadError('');
    setEventLoadedRequestId(null);
    if (!currentRequest?.timeevent_id) {
      setEventLoadStatus('not_required');
      return undefined;
    }
    let active = true;
    setEventLoadStatus('loading');
    fetchTimeeventById(currentRequest.timeevent_id)
      .then((loadedEvent) => {
        if (!active) return;
        if (!loadedEvent) {
          setEventLoadStatus('error');
          setEventLoadError(t('validation_workspace.flow_event_load_error'));
          return;
        }
        setEvent(loadedEvent);
        setEventLoadedRequestId(currentRequest.id);
        setEventLoadStatus('ready');
      })
      .catch(() => {
        if (!active) return;
        setEvent(null);
        setEventLoadStatus('error');
        setEventLoadError(t('validation_workspace.flow_event_load_error'));
      });
    return () => { active = false; };
  }, [currentRequest?.id, currentRequest?.timeevent_id, eventLoadAttempt, t]);

  const removeCurrentRequest = useCallback(() => {
    setRequests((previous) => previous.filter((request) => request.id !== currentRequest?.id));
    setTotalCount((previous) => Math.max(0, previous - 1));
    setIndex((previous) => Math.max(0, Math.min(previous, requests.length - 2)));
  }, [currentRequest?.id, requests.length]);

  const decide = useCallback(async (decision) => {
    if (!currentRequest || deciding) return;
    if (decision === 'reject' && !comment.trim()) {
      setError(t('validation_workspace.flow_rejection_comment_required'));
      return;
    }
    if (decision === 'approve' && !allItemsConfirmed) {
      setError(t('validation_workspace.flow_review_items_required'));
      return;
    }
    if (decision === 'approve' && !eventPreviewReady) {
      setError(t('validation_workspace.flow_event_load_error'));
      return;
    }
    setDeciding(true);
    setError('');
    try {
      await decideWorkflowHumanValidation(currentRequest.id, decision, {
        comment: comment.trim(),
        reviewItemsConfirmed: allItemsConfirmed,
      });
      const shouldLoadMore = requests.length === 1 && totalCount > 1;
      removeCurrentRequest();
      if (shouldLoadMore) {
        await loadRequests();
      }
    } catch (requestError) {
      setError(requestError?.message || t('validation_workspace.flow_decision_error'));
    } finally {
      setDeciding(false);
    }
  }, [
    allItemsConfirmed,
    comment,
    currentRequest,
    deciding,
    eventPreviewReady,
    loadRequests,
    removeCurrentRequest,
    requests.length,
    t,
    totalCount,
  ]);

  if (!isValidator) {
    return <Typography sx={{ p: 4 }}>{t('validation_workspace.access_denied')}</Typography>;
  }
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (!currentRequest && error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 720, mx: 'auto' }}>
        <Alert
          severity="error"
          action={<Button color="inherit" onClick={loadRequests}>{t('validation_workspace.flow_retry')}</Button>}
        >
          {error}
        </Alert>
      </Box>
    );
  }
  if (!currentRequest) {
    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 720, mx: 'auto' }}>
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
          <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
            {t('validation_workspace.flow_complete_title')}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {t('validation_workspace.flow_complete_description')}
          </Typography>
          <Button sx={{ mt: 3 }} onClick={() => navigate('/validate')}>
            {t('validation_workspace.split_validation_complete_action')}
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: { xs: 0.75, sm: 1.25, lg: 1.5 },
      minHeight: '100dvh',
      boxSizing: 'border-box',
      background: 'linear-gradient(135deg, #f8f9fd 0%, #f3f5fb 100%)',
    }}>
      <Paper
        elevation={0}
        sx={{
          px: { xs: 0.75, md: 1.5 },
          py: { xs: 0.65, md: 0.8 },
          mb: { xs: 1, md: 1.5 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.25, md: 1 },
          overflow: 'hidden',
          border: '1px solid rgba(87, 87, 176, 0.12)',
          borderRadius: 3,
          bgcolor: 'rgba(255, 255, 255, 0.82)',
          boxShadow: '0 8px 24px rgba(35, 38, 82, 0.07)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: { xs: 0.25, md: 1 } }}>
          <Typography
            variant="subtitle2"
            fontWeight={800}
            color="primary.dark"
            sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}
          >
            {t('validation_workspace.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexGrow: 1, minWidth: 78, maxWidth: { xs: 180, md: 300 } }}>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              aria-label={t('validation_workspace.flow_progress_aria')}
              sx={{
                flexGrow: 1,
                minWidth: 40,
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(87, 87, 176, 0.12)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #5757b0, #8c8cda)',
                },
              }}
            />
            <Typography
              variant="caption"
              color="primary.dark"
              fontWeight={800}
              sx={{ whiteSpace: 'nowrap', px: 0.8, py: 0.25, borderRadius: 1.5, bgcolor: 'rgba(87, 87, 176, 0.08)' }}
            >
              {index + 1} / {queueTotal}
            </Typography>
          </Box>
        </Box>
        <ActiveValidationLocksMenu
          locks={validationLocksForDisplay}
          username={username}
          buttonId="flow-validation-locks-button"
        />
        <Tooltip title={t('validation_workspace.flow_leave_tooltip')} arrow>
          <span>
            <IconButton
              size="small"
              disabled={deciding}
              onClick={() => navigate('/validate')}
              aria-label={t('validation_workspace.flow_back')}
              sx={{
                minWidth: { xs: 40, md: 36 },
                minHeight: { xs: 40, md: 36 },
                p: 0.75,
                borderRadius: 2,
                color: 'error.dark',
                '&:not(:disabled):hover': { bgcolor: 'rgba(211, 47, 47, 0.08)' },
              }}
            >
              <ExitToAppIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('validation_workspace.flow_previous_tooltip')} arrow>
          <span>
            <IconButton
              size="small"
              disabled={index === 0 || deciding}
              onClick={handlePrevious}
              aria-label={t('validation_workspace.previous')}
              sx={{
                minWidth: { xs: 40, md: 36 },
                minHeight: { xs: 40, md: 36 },
                p: 0.75,
                borderRadius: 2,
                color: 'text.secondary',
                '&:not(:disabled):hover': { bgcolor: 'rgba(87, 87, 176, 0.1)', color: 'primary.main' },
              }}
            >
              <NavigateBeforeIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('validation_workspace.flow_next_tooltip')} arrow>
          <span>
            <IconButton
              size="small"
              disabled={deciding || loadingMore || (index >= requests.length - 1 && requests.length >= totalCount)}
              onClick={handleNext}
              aria-label={t('validation_workspace.next')}
              sx={{
                minWidth: { xs: 40, md: 36 },
                minHeight: { xs: 40, md: 36 },
                p: 0.75,
                borderRadius: 2,
                color: 'text.secondary',
                '&:not(:disabled):hover': { bgcolor: 'rgba(87, 87, 176, 0.1)', color: 'primary.main' },
              }}
            >
              <NavigateNextIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {currentRequest.dispatch_error && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>{currentRequest.dispatch_error}</Alert>
      )}
      {isEventValidation && !currentRequest.timeevent_id && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {t('validation_workspace.flow_event_missing')}
        </Alert>
      )}

      <Box sx={{
        display: 'grid',
        height: { xs: 'auto', lg: 'calc(100dvh - 86px)' },
        minHeight: 0,
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(560px, 65%) minmax(360px, 35%)' },
        gridTemplateRows: { xs: 'minmax(280px, 45dvh) auto', lg: 'minmax(0, 1fr)' },
        gap: { xs: 1, md: 1.5 },
      }}>
        <Paper
          elevation={0}
          sx={{
            minHeight: { xs: 280, lg: 0 },
            height: { xs: '45dvh', lg: '100%' },
            overflow: 'hidden',
            p: 0,
            border: '1px solid rgba(35, 38, 82, 0.12)',
            borderRadius: 3,
            bgcolor: '#1f2433',
            boxShadow: '0 14px 34px rgba(35, 38, 82, 0.12)',
          }}
        >
          {event && (
            <React.Suspense
              fallback={(
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              )}
            >
              <ViewerComponent
                open
                embedded
                onClose={() => {}}
                filename={String(event.eventsource || '').split(/[\\/]/).pop()}
                eventId={event.id}
                filePath={event.eventsource}
                timeevent={event}
                onPdfUrlChange={setPdfViewerUrl}
              />
            </React.Suspense>
          )}
          {!event && eventLoadStatus === 'loading' && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, height: '100%', color: 'white' }}>
              <CircularProgress size={22} />
              <Typography color="inherit">
                {t('validation_workspace.flow_event_loading')}
              </Typography>
            </Box>
          )}
          {!event && !isEventValidation && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3, textAlign: 'center' }}>
              <Typography color="rgba(255, 255, 255, 0.72)">
                {t('validation_workspace.flow_project_context')}
              </Typography>
            </Box>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            minHeight: 0,
            overflow: { xs: 'visible', lg: 'auto' },
            p: { xs: 1.25, lg: 2 },
            border: '1px solid rgba(87, 87, 176, 0.12)',
            borderRadius: 3,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            boxShadow: '0 14px 34px rgba(35, 38, 82, 0.08)',
          }}
        >
          <Box sx={{
            mb: 2,
            p: { xs: 1, lg: 1.25 },
            border: '1px solid rgba(87, 87, 176, 0.1)',
            borderRadius: 2.5,
            background: 'linear-gradient(135deg, rgba(87, 87, 176, 0.08), rgba(255, 255, 255, 0.72))',
          }}>
            <Typography variant="h6" fontWeight={800} color="primary.dark">
              {t('validation_workspace.flow_context_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {isEventValidation
                ? (event?.eventsource || currentRequest.timeevent_id)
                : t('validation_workspace.flow_project_context')}
            </Typography>
          </Box>
          {event && (
            <Box sx={{ mb: 1 }}>
              <React.Suspense fallback={<CircularProgress size={24} />}>
                <TimeEvent
                  timeevent={event}
                  showChartFunction={noopEventAction}
                  setFilteredTimeEvents={noopSetFilteredTimeEvents}
                  navigateTo={noopEventAction}
                  compact
                  maxVisibleTags={12}
                  maxVisibleAttachments={4}
                  showThumbnail={false}
                  openViewerOnHover={false}
                />
              </React.Suspense>
            </Box>
          )}
          {eventLoadStatus === 'error' && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={(
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setEventLoadAttempt((attempt) => attempt + 1)}
                >
                  {t('validation_workspace.flow_retry')}
                </Button>
              )}
            >
              {eventLoadError || t('validation_workspace.flow_event_load_error')}
            </Alert>
          )}
          <Box sx={{
            mb: 2,
            p: { xs: 1, lg: 1.25 },
            border: '1px solid rgba(87, 87, 176, 0.1)',
            borderRadius: 2.5,
            background: 'linear-gradient(135deg, rgba(87, 87, 176, 0.08), rgba(255, 255, 255, 0.72))',
          }}>
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 800 }}>
              {t('validation_workspace.flow_validation_title')}
            </Typography>
            <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
              {currentRequest.title}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {currentRequest.instructions}
            </Typography>
          </Box>

          {reviewItems.length > 0 && (
            <Box sx={{ mt: 2, p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 800 }}>
                {t('validation_workspace.flow_checklist')}
              </Typography>
              <Box sx={{ display: 'grid', gap: 0.75, mt: 1 }}>
                {reviewItems.map((item, itemIndex) => {
                  const isChecked = checkedItems.includes(itemIndex);
                  return (
                    <FormControlLabel
                      key={`${itemIndex}-${item}`}
                      control={(
                        <Checkbox
                          checked={isChecked}
                          onChange={(checkboxEvent) => setCheckedItems((previous) => (
                            checkboxEvent.target.checked
                              ? [...previous, itemIndex]
                              : previous.filter((indexValue) => indexValue !== itemIndex)
                          ))}
                        />
                      )}
                      label={item}
                      sx={{
                        display: 'flex',
                        mx: 0,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1.5,
                        bgcolor: isChecked ? 'rgba(46, 125, 50, 0.07)' : 'transparent',
                        '& .MuiFormControlLabel-label': { fontSize: '0.9rem' },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            minRows={3}
            value={comment}
            onChange={(commentEvent) => setComment(commentEvent.target.value)}
            label={t('validation_workspace.flow_comment')}
            helperText={t('validation_workspace.flow_comment_help')}
            sx={{ mt: 2 }}
          />
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
            <Button
              color="error"
              variant="outlined"
              startIcon={<CloseIcon />}
              disabled={deciding || !comment.trim()}
              onClick={() => decide('reject')}
            >
              {t('validation_workspace.flow_reject')}
            </Button>
            <Button
              color="success"
              variant="contained"
              startIcon={<CheckCircleIcon />}
              disabled={deciding || !allItemsConfirmed || !eventPreviewReady}
              onClick={() => decide('approve')}
            >
              {t('validation_workspace.flow_approve')}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
